import { useEffect, useState } from "react";

/**
 * Página do link de convite de equipe (epico 3).
 *
 * Servida pelo catch-all do SPA em `/aceitar-convite?token=...`. Mostra pra
 * qual organização a pessoa foi convidada antes de pedir qualquer dado -
 * mesmo princípio de transparência do ResetPasswordPage.
 */
interface Preview {
  valid: boolean;
  orgName?: string;
  email?: string;
  hasAccount?: boolean;
}

export function AcceptInvitePage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const [preview, setPreview] = useState<Preview | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPreview({ valid: false });
      return;
    }
    fetch(`/api/auth/invite-preview?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((j: Preview) => setPreview(j))
      .catch(() => setPreview({ valid: false }));
  }, [token]);

  const accept = async (extra?: { name?: string; password?: string; confirm?: string }) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/accept-invite", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, ...extra }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "Não foi possível aceitar o convite.");
        return;
      }
      // a rota já autentica: recarregar leva direto para dentro do app, na org certa
      window.location.href = "/";
    } catch {
      setError("Falha de conexão.");
    } finally {
      setBusy(false);
    }
  };

  const submitNewAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("As senhas não conferem.");
      return;
    }
    void accept({ name, password, confirm });
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Convite de equipe</h1>

        {!preview ? (
          <p className="auth-sub">Carregando…</p>
        ) : !preview.valid ? (
          <>
            <p className="auth-sub">
              Este convite é inválido ou já expirou. Peça a um administrador da organização para
              enviar um novo.
            </p>
            <a className="auth-submit" href="/" style={{ display: "block", textAlign: "center" }}>
              Ir para o login
            </a>
          </>
        ) : (
          <>
            <p className="auth-sub">
              Você foi convidado(a) para entrar na organização <strong>{preview.orgName}</strong>{" "}
              como <strong>{preview.email}</strong>.
            </p>

            {preview.hasAccount ? (
              <>
                <p className="auth-sub">
                  Já existe uma conta com este e-mail - aceitar o convite move essa conta para
                  esta organização.
                </p>
                {error && <div className="auth-error">{error}</div>}
                <button className="auth-submit" disabled={busy} onClick={() => void accept()} type="button">
                  {busy ? "Entrando…" : "Aceitar e entrar"}
                </button>
              </>
            ) : (
              <form onSubmit={submitNewAccount} className="auth-form">
                <label className="auth-field">
                  <span>Nome</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Como quer ser chamado"
                    autoComplete="name"
                  />
                </label>
                <label className="auth-field">
                  <span>Senha</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </label>
                <label className="auth-field">
                  <span>Repetir a senha</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repita para confirmar"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </label>
                {error && <div className="auth-error">{error}</div>}
                <button type="submit" className="auth-submit" disabled={busy}>
                  {busy ? "Criando…" : "Criar conta e entrar"}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
